'use client';

/**
 * Deploy-skew self-healing. After a blue/green promote, tabs that loaded the
 * OLD HTML shell still ask for old hashed chunks on their next navigation —
 * which the new pods no longer serve — and the app hard-crashes with
 * "Failed to load chunk …". That one failure mode is ~85% of our error
 * volume (1,308 events / 15 fingerprints on 8/4–8/11: bursts of 27–280,
 * each burst exactly bracketing a deploy window, all from `module 649271`
 * and friends — stale clients, not code bugs).
 *
 * The honest fix for a stale shell is a fresh shell: reload once. Guarded
 * to once a minute per tab so a genuinely broken asset (CDN outage, adblock)
 * degrades to the old error instead of a reload loop.
 */

import { useEffect } from 'react';

const GUARD_KEY = 'chunk-reload-at';
const GUARD_MS = 60_000;

function isChunkLoadFailure(message: unknown): boolean {
  return typeof message === 'string'
    && /(Failed to load chunk|ChunkLoadError|Loading chunk [\w-]+ failed|error loading dynamically imported module)/i.test(message);
}

function reloadOnce(): void {
  let last = 0;
  try { last = Number(sessionStorage.getItem(GUARD_KEY) ?? 0); } catch { /* private mode */ }
  if (Date.now() - last < GUARD_MS) return; // already tried — let it surface
  try { sessionStorage.setItem(GUARD_KEY, String(Date.now())); } catch { /* private mode */ }
  window.location.reload();
}

export function ChunkReloadRecovery() {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      if (isChunkLoadFailure(e.message) || (e.error && isChunkLoadFailure(String(e.error?.message)))) {
        reloadOnce();
      }
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const m = e.reason && typeof e.reason === 'object' ? (e.reason as { message?: string }).message : e.reason;
      if (isChunkLoadFailure(String(m ?? ''))) reloadOnce();
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);
  return null;
}
